import isEmpty from "lodash/isEmpty";
import { LegacyForm } from "PFApp/components/form/legacy_form";
import ProfileEditPanel from "PFApp/profiles/edit/profile_edit_panel";
import { getProfileSectionText } from "PFApp/profiles/edit/profile_edit_section_titles";
import css from "PFApp/profiles/edit/sections/section.module.scss";
import ProfileLinkedInConnectButton from "PFApp/profiles/profile_linkedin_connect_button";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { useSignOut } from "PFApp/use_session";
import { Alert } from "PFComponents/alert";
import { Button } from "PFComponents/button";
import { LoadingDots } from "PFComponents/loading_dots";
import { InputFieldSet } from "PFComponents/text/input_field_set";
import { useCurrentAccount } from "PFCore/hooks/queries/account";
import { useForgotPassword } from "PFCore/hooks/queries/auth/use_forgot_password";
import { useCurrentProfile } from "PFCore/hooks/queries/profile";
import { editCurrentProfile } from "PFCore/services/current_profile";
import { CurrentProfile, Profile } from "PFTypes";
import { useState } from "react";
import { useTranslation } from "react-i18next";

type CredentialsSectionProps = {
  profile?: Profile;
  handleProfileUpdate: () => void;
};

const CredentialsSection = ({ profile, handleProfileUpdate }: CredentialsSectionProps) => {
  const { data: currentAccount } = useCurrentAccount();
  const { data: currentProfile } = useCurrentProfile();
  const { showGrowl } = useGrowlStoreActions();
  const { t } = useTranslation("profiles", { keyPrefix: "edit.sections" });

  const [email, setEmail] = useState(currentProfile.email);
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [emailErrors, setEmailErrors] = useState<Record<string, string>>({});

  const { signOut } = useSignOut();

  const {
    mutate: forgotPassword,
    isPending: isForgotPasswordLoading,
    isSuccess: isForgotPasswordSuccess,
    isError: isForgotPasswordError,
    isIdle: isForgotPasswordIdle
  } = useForgotPassword();

  const handleEditEmail = () => editCurrentProfile({ email });
  const handleEditPassword = () =>
    editCurrentProfile({
      current_password: password,
      password: newPassword,
      password_confirmation: newPassword
    } as Partial<CurrentProfile>);

  const resetPassword = () => {
    forgotPassword({
      accountId: currentAccount.id,
      email: profile!.email
    });
  };

  const handleEmailSuccess = () => {
    handleProfileUpdate();
    setEmailErrors({});
    showGrowl({ message: t("credentials.successes.email") });
  };

  const handleEmailError = (errors: Record<string, string>) => setEmailErrors(errors);

  const handleSuccess = () => {
    handleProfileUpdate();
    setEmailErrors({});
    showGrowl({ message: t("credentials.successes.password") });
  };

  const handleError = (errors: Record<string, string>) => {
    if (errors.currentPassword) {
      signOut({
        error: t("credentials.passwordError")
      });
      return null;
    } else {
      setErrors(errors);
    }
  };

  const renderForgotLink = () =>
    isForgotPasswordIdle ? (
      <small>
        {t("credentials.forgotPassword")}{" "}
        <Button kind="text" data-qa-id="profile-edit-password-forgot" onClick={resetPassword}>
          {t("credentials.resetPassword")}
        </Button>
      </small>
    ) : (
      <Alert style={{ display: "inline-block" }} kind={isForgotPasswordError ? "error" : "warning"}>
        {isForgotPasswordLoading && <LoadingDots />}
        {isForgotPasswordSuccess && t("credentials.willReceiveEmail")}
        {isForgotPasswordError && t("credentials.anErrorOccured")}
      </Alert>
    );

  return (
    <ProfileEditPanel title={getProfileSectionText("credentials")}>
      {profile && (
        <div className={css.connectButton}>
          <ProfileLinkedInConnectButton profile={profile} />
        </div>
      )}

      <h3 className={css.header} style={{ paddingTop: 0 }}>
        {t("credentials.changeYourEmail")}
      </h3>
      <LegacyForm
        onSuccess={handleEmailSuccess}
        onError={handleEmailError}
        onSubmit={handleEditEmail}
        qaIdPrefix="profile-edit-credentials-email"
        isDirty={profile?.email !== email}
      >
        <div data-qa-id="profile-edit-credentials-email">
          <InputFieldSet
            label={t("credentials.emailAddress")}
            error={emailErrors.email}
            tip={t("credentials.changeEmailTip")}
            value={email}
            onChange={setEmail}
          />
        </div>
      </LegacyForm>

      <h3 className={css.header}>{t("credentials.changeYourPassword")}</h3>
      <LegacyForm
        isDirty={!isEmpty(password) || !isEmpty(newPassword)}
        onSuccess={handleSuccess}
        onError={handleError}
        onSubmit={handleEditPassword}
        qaIdPrefix="profile-edit-credentials-password"
      >
        <div style={{ textAlign: "right", marginTop: 12, marginBottom: 20 }}>{renderForgotLink()}</div>

        <div data-qa-id="profile-edit-credentials-current-password">
          <InputFieldSet
            label={t("credentials.currentPassword")}
            error={errors.currentPassword}
            inputType="password"
            value={password}
            onChange={setPassword}
          />
        </div>
        <div data-qa-id="profile-edit-credentials-new-password">
          <InputFieldSet
            label={t("credentials.newPassword")}
            error={errors.password}
            tip={t("credentials.newPasswordTip")}
            showPasswordToggle={true}
            inputType="password"
            onChange={setNewPassword}
          />
        </div>
      </LegacyForm>
    </ProfileEditPanel>
  );
};

export default CredentialsSection;
